/**
 * Task / Project Management — permission utilities.
 *
 * ROUTE/NAV ACCESS: the `/apps/tasks` route and its sidebar nav item (see
 * `src/router/routes/apps.js` / `src/navigation/vertical/apps-and-pages.js`)
 * are gated on their own dedicated CASL subject, `task_management` (action
 * `read`) — this used to piggyback on the Dashboard's `main_dashboard`
 * subject as a stopgap, but the module now has its own. Since abilities are
 * backend-driven (`userData.abilities` on login) and the backend may not
 * yet grant `task_management` per role, it's also added as a
 * default-for-all-users entry in `initialAbility`
 * (`src/libs/acl/config.js`) so existing behavior doesn't regress until the
 * backend formally issues it — see the comment there for details.
 *
 * That route-level ACL gate is a completely separate concern from
 * everything below in this file: "who is a manager" / "who is on my team"
 * (used for task assignment/edit/priority permissions WITHIN the module,
 * not for whether a user can open it at all) is derived purely from the SAME
 * data source the Attendance module already uses:
 *   - `userService.getAllTeamMembersList(emp_id)` (see
 *     `src/views/dashboard/attendance/mixins/teamDataMixin.js` ->
 *     `loadTeamMembers()`) — returns the logged-in user's direct reports.
 * A user is treated as a "manager" for the Task module simply when that
 * call returns at least one active direct report — no separate role/ACL
 * check, per the task spec ("reuse the existing team/manager lookup ...,
 * not a new role system").
 *
 * Permission model:
 *   - Anyone can create a task, and assign it to themselves or (if they have
 *     direct reports) to one of those direct reports.
 *   - Only the task's CREATOR (`createdBy` / `taskOwnerEmpId`) may edit
 *     details, change priority, or delete a task. Assignees and co-assignees
 *     whose task was created by someone else may still change STATUS (and
 *     mark complete) — all other fields stay locked.
 *   - PING / ask-for-update is a SEPARATE concern from editing: any manager
 *     of the assignee may ping that employee about ANY of their tasks.
 *   - EDIT rights (above) and LIST VISIBILITY are deliberately different
 *     questions. A manager who creates a task and assigns it to a direct
 *     report keeps full edit rights over it (`isOwnTask` — they're the
 *     creator) — but that task should surface in their "My Team's Tasks"
 *     view, not clutter their own personal "My Tasks" queue as if it were
 *     theirs to do. See `isMyTasksListTask` vs. `isOwnTask` in
 *     `visibleTasks` below.
 */

import ability from '@/libs/acl/ability'
import { taskAssigneeEmpId, taskOwnerEmpId, taskOwnerName } from './task-config'
import { isProjectResponsibilityTask } from './project-responsibility-tasks'

function tryParseUserData() {
  try {
    return JSON.parse(localStorage.getItem('userData')) || {}
  } catch (e) {
    return {}
  }
}

/**
 * Route/nav/module access — mirrors `$can('read', 'task_management')` on
 * `/apps/tasks`, sidebar Tasks, and UserDropdown. CASL `manage` on the same
 * subject (or `all`) satisfies `read`.
 */
export function canAccessTaskManagement() {
  return ability.can('read', 'task_management')
}

/** `taskOwnerName` falls back to `'-'` when nothing's resolvable — treat that the same as "unknown" here. */
function creatorDisplayName(task) {
  const name = taskOwnerName(task)
  return name && name !== '-' ? name : 'your manager'
}

export function getEmpId(user) {
  const data = user || tryParseUserData()
  const value = data?.emp_id ?? data?.empId
  return value === undefined || value === null ? null : String(value)
}

/** Normalizes a team-member list (varying shapes from userService) to a Set of emp_id strings. */
export function teamMemberIdSet(teamMembers) {
  const ids = (teamMembers || [])
    .map(member => member?.emp_id ?? member?.id)
    .filter(id => id !== undefined && id !== null)
    .map(String)
  return new Set(ids)
}

/** True when the logged-in user has at least one active direct report loaded. */
export function isManager(teamMembers) {
  return Array.isArray(teamMembers) && teamMembers.length > 0
}

export function isTaskCoAssignee(user, task) {
  const empId = getEmpId(user)
  if (!empId || !task) return false
  const roster = Array.isArray(task.coAssignees) ? task.coAssignees : []
  return roster.some(person => person && String(person.empId) === empId)
}

export function isOwnTask(user, task) {
  const empId = getEmpId(user)
  if (!empId || !task) return false
  return taskAssigneeEmpId(task) === empId
    || taskOwnerEmpId(task) === empId
    || isTaskCoAssignee(user, task)
}

/**
 * True when `task` belongs in the current user's PERSONAL "My Tasks" list
 * specifically (the non-team-mode view in `TaskBoard.vue`) — i.e. the user
 * is the task's ASSIGNEE, or it's a self-created/legacy task with no
 * assignee recorded at all (falls back to the creator, same as before).
 *
 * Deliberately NARROWER than `isOwnTask()` above: a manager who creates a
 * task and assigns it to one of their direct reports still satisfies
 * `isOwnTask()` for the manager themselves (they ARE the creator) — which
 * is exactly right for EDIT/DELETE/status permissions (a creator keeps
 * full rights over a task they set up, however it's currently assigned;
 * see `canEditTaskDetails`/`canChangeStatus`/`canDeleteTask`, all still
 * built on `isOwnTask`, unchanged). But that same task is the EMPLOYEE's
 * to work on day-to-day, and showing it in the manager's own "My Tasks"
 * queue too — on top of it correctly appearing in "My Team's Tasks" via
 * `isTeamTask()` — was a reported bug: a delegated task cluttering the
 * manager's personal list as if it were theirs to do.
 *
 * Used ONLY by `visibleTasks()` below for this one list-membership
 * decision — never substitute this for `isOwnTask()` in a permission
 * check, or a manager would lose their (correct, unrelated) edit rights
 * over tasks they created for someone else.
 */
export function isMyTasksListTask(user, task) {
  const empId = getEmpId(user)
  if (!empId || !task) return false
  if (isTaskCoAssignee(user, task)) return true
  const assignee = taskAssigneeEmpId(task)
  if (assignee == null) return taskOwnerEmpId(task) === empId
  return assignee === empId
}

/** True when the logged-in user is the task's creator ("the assigning manager", or a self-creator). */
export function isTaskCreator(user, task) {
  const empId = getEmpId(user)
  if (!empId || !task) return false
  const owner = taskOwnerEmpId(task)
  return owner != null && owner === empId
}

/**
 * True when the current user is ONLY the assignee of `task` — i.e. someone
 * ELSE (a manager) created/assigned it to them. This is the "details
 * locked" case: the employee can still change Status, but not the fields
 * the manager set up (title/description/priority/due date/assignee).
 *
 * False for:
 *   - self-created tasks (creator === assignee),
 *   - tasks with no recorded creator at all (legacy/local data — treated as
 *     fully editable, same as before this permission existed),
 *   - the creator themselves, regardless of who it's currently assigned to.
 */
export function isAssignedByOthers(user, task) {
  if (!task) return false
  const empId = getEmpId(user)
  if (!empId) return false
  const owner = taskOwnerEmpId(task)
  if (owner == null || owner === empId) return false
  return taskAssigneeEmpId(task) === empId || isTaskCoAssignee(user, task)
}

/** True when `task`'s assignee is one of the given manager's direct reports. */
export function isTeamTask(task, teamMembers) {
  if (!task) return false
  const ids = teamMemberIdSet(teamMembers)
  const assignee = taskAssigneeEmpId(task)
  const owner = taskOwnerEmpId(task)
  return (assignee !== null && ids.has(assignee)) || (owner !== null && ids.has(owner))
}

/**
 * Tasks visible to the logged-in user.
 *
 * - `includeTeam` false ("My Tasks"): only tasks that are PERSONALLY the
 *   user's to work on — `isMyTasksListTask()` (assignee === user, or a
 *   self-created/legacy task with no assignee recorded). A task the user
 *   created but assigned to someone else does NOT show here — see that
 *   function's doc comment for why this must be narrower than `isOwnTask`.
 * - `includeTeam` true ("My Team's Tasks"): the same personal tasks
 *   PLUS anything owned (created OR assigned) by a direct report — this
 *   is where a manager sees tasks they delegated to their team.
 */
export function visibleTasks(allTasks, user, teamMembers, includeTeam) {
  if (!Array.isArray(allTasks)) return []
  return allTasks.filter(task => {
    if (task?.isDeleted) return false
    if (includeTeam) return isOwnTask(user, task) || isTeamTask(task, teamMembers)
    return isMyTasksListTask(user, task)
  })
}

/**
 * Full detail-field editability — creator only. Legacy tasks with no
 * recorded creator are treated as editable by the assignee (same as before).
 */
export function canEditTaskDetails(user, task) {
  if (!task) return false
  if (isProjectResponsibilityTask(task)) return false
  const owner = taskOwnerEmpId(task)
  if (owner == null) return isOwnTask(user, task)
  return isTaskCreator(user, task)
}

/** Status changes — creator, or assignee/co-assignee when assigned by someone else. */
export function canChangeStatus(user, task) {
  if (isProjectResponsibilityTask(task)) {
    return isMyTasksListTask(user, task)
  }
  return canEditTaskDetails(user, task) || isAssignedByOthers(user, task)
}

/**
 * Recurrence setup (the "Recurring" toggle + frequency/interval/days/end
 * date in `TaskFormModal.vue`) is just another task DETAIL, same bucket as
 * title/description/priority/due date/assignee — a plain alias of
 * `canEditTaskDetails`, kept as its own named export purely so call sites
 * that specifically mean "can this user configure recurrence" read clearly
 * rather than reusing a generically-named detail check by coincidence.
 */
export function canConfigureRecurrence(user, task) {
  return canEditTaskDetails(user, task)
}

/** Priority — creator only (no manager override). */
export function canChangePriority(user, task, teamMembers) {
  if (isProjectResponsibilityTask(task)) return false
  return canEditTaskDetails(user, task)
}

/**
 * Ping / ask-for-update — deliberately SEPARATE from `canEditTaskDetails`.
 *
 * A manager may ping an employee about ANY of that employee's tasks when
 * the task's assignee is one of the manager's direct reports. Does NOT
 * require `createdBy ===` the current manager (that rule is only for
 * editing title/description/etc.).
 */
export function canPingTask(user, task, teamMembers) {
  if (!task || !isManager(teamMembers)) return false
  const assignee = taskAssigneeEmpId(task)
  if (assignee == null) return false
  return teamMemberIdSet(teamMembers).has(String(assignee))
}

/**
 * Per-employee "Ping for update" (all their open tasks) — same direct-report
 * gate as `canPingTask`, without needing a specific task row.
 */
export function canPingEmployee(empId, teamMembers) {
  if (empId == null || !isManager(teamMembers)) return false
  return teamMemberIdSet(teamMembers).has(String(empId))
}

/**
 * Deleting is a superset of editing details, so it follows the same lock:
 * an employee whose task was assigned by someone else cannot delete it
 * either (only update its status) — only the creator (or a self-creator)
 * can remove a task.
 */
export function canDeleteTask(user, task) {
  if (isProjectResponsibilityTask(task)) return false
  return canEditTaskDetails(user, task)
}

/** Anyone can create a task; assignee choices are self + (if manager) direct reports. */
export function canCreateTask() {
  return true
}

export function assignableEmpIds(user, teamMembers) {
  const empId = getEmpId(user)
  const ids = Array.from(teamMemberIdSet(teamMembers))
  return empId ? [empId, ...ids.filter(id => id !== empId)] : ids
}

export function canAssignTo(candidateEmpId, user, teamMembers) {
  if (candidateEmpId == null) return false
  return assignableEmpIds(user, teamMembers).includes(String(candidateEmpId))
}

/**
 * A manager may drag-reassign a task between their direct reports' columns
 * in the "My Team's Tasks" per-member Kanban view. Mirrors
 * `canChangePriority`'s manager-override bypass: the mover doesn't need to
 * be the task's creator/assignee, they just need (a) the task to already be
 * theirs to manage (their own, or one of their team's) and (b) the drop
 * target to be a valid assignee (themselves or one of their direct
 * reports) — never an arbitrary emp_id typed in some other way.
 */
export function canReassignTask(user, task, teamMembers, targetEmpId) {
  if (!task || targetEmpId == null) return false
  if (!isManager(teamMembers)) return false
  if (!(isOwnTask(user, task) || isTeamTask(task, teamMembers))) return false
  return canAssignTo(targetEmpId, user, teamMembers)
}

export function getEditDenialReason(user, task) {
  if (!task) return 'No task selected.'
  if (!canEditTaskDetails(user, task)) {
    return 'Only the task creator can edit this task.'
  }
  return null
}

export function getStatusDenialReason(user, task) {
  if (!task) return 'No task selected.'
  if (!canChangeStatus(user, task)) {
    return 'Only the task creator can change this task\'s status.'
  }
  return null
}

/**
 * Shown next to locked fields when the current user is not the creator.
 */
export function getLockedFieldsMessage(user, task) {
  if (!task || isTaskCreator(user, task)) return null
  if (isAssignedByOthers(user, task)) {
    return `This task was assigned by ${creatorDisplayName(task)} — you can only update its status.`
  }
  return 'Only the creator can update this task.'
}

export function getPriorityDenialReason(user, task, teamMembers) {
  if (!task) return 'No task selected.'
  if (!canChangePriority(user, task, teamMembers)) {
    return 'Only the task creator can change priority.'
  }
  return null
}

export function getPingDenialReason(user, task, teamMembers) {
  if (!task) return 'No task selected.'
  if (!canPingTask(user, task, teamMembers)) {
    return 'Only a manager of the assignee can ask for an update on this task.'
  }
  return null
}

export function getReassignDenialReason(user, task, teamMembers, targetEmpId) {
  if (!task) return 'No task selected.'
  if (!canReassignTask(user, task, teamMembers, targetEmpId)) {
    return 'You can only reassign your team\'s tasks to yourself or one of your direct reports.'
  }
  return null
}

export default {
  getEmpId,
  canAccessTaskManagement,
  teamMemberIdSet,
  isManager,
  isOwnTask,
  isMyTasksListTask,
  isTaskCreator,
  isAssignedByOthers,
  isTeamTask,
  visibleTasks,
  canEditTaskDetails,
  canChangeStatus,
  canConfigureRecurrence,
  canChangePriority,
  canPingTask,
  canPingEmployee,
  canDeleteTask,
  canCreateTask,
  assignableEmpIds,
  canAssignTo,
  canReassignTask,
  getEditDenialReason,
  getStatusDenialReason,
  getPriorityDenialReason,
  getPingDenialReason,
  getReassignDenialReason,
  getLockedFieldsMessage,
}
